import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import storage from "@provider/storage";
import type {
  AboutUsStoryblok,
  AuthStoryblok,
  ContactStoryblok,
  FooterStoryblok,
  HomeStoryblok,
  HowItWorksStoryblok,
} from "@type/storyblock-types.d.ts";
import StoryblokClient from "storyblok-js-client";
import { useAppState } from "@utils";
import { Platform } from "react-native";
import { queryClient } from "@provider/query-client";

const Storyblok = new StoryblokClient({
  accessToken: "5z9crQOQfOAJXcEzPd6q4Att",
  cache: {
    clear: "auto",
    type: "memory",
  },
});

interface StoryBlokType {
  name: string;
  created_at: string;
  published_at: string;
  id: number;
  uuid: string;
  content: AboutUsStoryblok | AuthStoryblok | ContactStoryblok | FooterStoryblok | HomeStoryblok | HowItWorksStoryblok;
  slug: string;
  full_slug: string;
  sort_by_date: any;
  position: number;
  tag_list: any[];
  is_startpage: boolean;
  parent_id: any;
  meta_data: any;
  group_id: string;
  first_published_at: string;
  release_id: any;
  lang: string;
  path: any;
  alternates: any[];
  default_full_slug: any;
  translated_slugs: any;
}

const StoryBlokContext = React.createContext<StoryBlokType[]>(null);

function safeSave(key: string, value: any) {
  try {
    storage.set(key, JSON.stringify(value));
  } catch (error) {
    console.log(error);
  }
}

function safeParse(key: string) {
  try {
    return JSON.parse(storage.getString(key));
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

const key = "story-blok";
const getQueryKey = (enabled: boolean) => [key, enabled];
const initialData = safeParse(key);

const storiesQuery = {
  queryFn: async () => {
    const all = await Storyblok.getAll("cdn/stories", {});
    console.log("GET storyblok stories");
    safeSave(key, all);
    return all;
  },
  placeholderData: initialData ? initialData : undefined,
};

queryClient.prefetchQuery({ ...storiesQuery, staleTime: 1000, queryKey: getQueryKey(true) });

export function StoryBlokProvider({ children }: { children: any }) {
  const appState = useAppState();
  const enabled = Platform.select({ web: true, default: appState === "active" });

  const { data } = useQuery({
    ...storiesQuery,
    queryKey: getQueryKey(enabled),
    enabled: Platform.select({ web: true, default: enabled }),
  });

  return <StoryBlokContext.Provider value={data}>{data ? children : null}</StoryBlokContext.Provider>;
}

function useStoryBlokStories() {
  return React.useContext(StoryBlokContext);
}

export function useStoryBlokStoriesByComponent<T>(component: string) {
  const stories = useStoryBlokStories();
  return React.useMemo(
    () =>
      (stories || [])
        .filter((story) => (story.content as any)?.component === component)
        .map((story) => ({ ...story, content: story.content as T })),
    [stories, component]
  );
}

export function useStoryBlokStoryBySlug<T>(fullSlug: string) {
  const stories = useStoryBlokStories();
  const story = stories?.find((s) => s.full_slug === fullSlug || s.slug === fullSlug);
  return story ? { ...story, content: story.content as T } : undefined;
}

export function useStoryBlokStory<T>(slug: string) {
  const stories = useStoryBlokStories();
  const story = stories?.find((story) => story.slug === slug);
  return story?.content as T;
}
